import { get } from "aws-amplify/api";
import Moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { createColumnHelper } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import Select from "react-select";
import { PacmanLoader } from "react-spinners";
import { GenericTable } from "components/GenericTable";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import "containers/common/List.css";
import "containers/common/Table.css";
import "react-datepicker/dist/react-datepicker.css";
import "./SaleSheetFollowUpQueue.css";

const columnHelper = createColumnHelper();

function SaleSheetFollowUpQueue({ isAuthenticated }) {
  const [agents, setAgents] = useState([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [cityscopeOptions, setCityscopeOptions] = useState([]);
  const [selectedCityscope, setSelectedCityscope] = useState([]);
  const [followUpDate, setFollowUpDate] = useState(() => new Date());
  const [agentName, setAgentName] = useState("");

  const createId = useMemo(() => createElementIdFactory("salesheet-followup-queue"), []);
  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId, "agents"),
    [createId]
  );

  const handleError = useCallback((error) => {
    setErrorMessage(String(error));
    setShowAlert(true);
  }, []);

  const getCityscopes = useCallback(async () => {
    const response = await get({
      apiName: "colin",
      path: "/cityscopes/dropdown",
    }).response;
    const payload = await response.body.json();
    return (payload || []).map((option) => ({
      label: option.label ?? option.cityscope ?? option.value,
      value: option.value ?? option.cityscope ?? option.label,
    }));
  }, []);

  const getAgents = useCallback(async (filter) => {
    const response = await get({
      apiName: "colin",
      path: "/queues/salesheets",
      options: {
        queryParams: filter,
      },
    }).response;
    const payload = await response.body.json();
    return (payload || []).map((row) => ({
      ...row,
      selected: Boolean(row.selected),
    }));
  }, []);

  const normaliseAgentId = useCallback((id) => {
    if (id === null || id === undefined || id === "") {
      return 0;
    }

    const numeric = Number(id);
    return Number.isNaN(numeric) ? id : numeric;
  }, []);

  const toCityscopeCsv = useCallback((selected, allOptions) => {
    const selectedValues = (selected || []).map((option) => option.value).filter(Boolean);
    if (selectedValues.length > 0) {
      return selectedValues.join(",");
    }

    return (allOptions || []).map((option) => option.value).filter(Boolean).join(",");
  }, []);

  const toSelectedCityscopeCsv = useCallback((selected) => {
    const selectedValues = (selected || []).map((option) => option.value).filter(Boolean);
    return selectedValues.length > 0 ? selectedValues.join(",") : "";
  }, []);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const localCityscopes = await getCityscopes();
      setCityscopeOptions(localCityscopes);

      const localAgents = await getAgents({
        followupdate: Moment().format(),
      });
      setAgents(localAgents);
      setSelectedAgentIds(
        localAgents
          .filter((row) => row.selected)
          .map((row) => normaliseAgentId(row.entity_id_agent))
      );
      setIsLoading(false);
    } catch (error) {
      handleError(error);
      setIsLoading(false);
    }
  }, [getAgents, getCityscopes, handleError, normaliseAgentId]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    document.title = "Sale Sheet Follow Up Queue";
    loadData();
  }, [isAuthenticated, loadData]);

  const toggleSelected = useCallback((entityId, checked) => {
    const targetId = normaliseAgentId(entityId);

    setAgents((previous) =>
      previous.map((row) =>
        row.entity_id_agent === entityId
          ? {
              ...row,
              selected: checked,
            }
          : row
      )
    );

    setSelectedAgentIds((previous) => {
      if (checked) {
        return Array.from(new Set([...previous, targetId]));
      }

      return previous.filter((id) => id !== targetId);
    });
  }, [normaliseAgentId]);

  const selectAll = useCallback(() => {
    setAgents((previous) => {
      const allIds = previous.map((row) => normaliseAgentId(row.entity_id_agent));
      setSelectedAgentIds(Array.from(new Set(allIds)));
      return previous.map((row) => ({ ...row, selected: true }));
    });
  }, [normaliseAgentId]);

  const deselectAll = useCallback(() => {
    setAgents((previous) => previous.map((row) => ({ ...row, selected: false })));
    setSelectedAgentIds([]);
  }, []);

  const getBrowseListAgentIds = useCallback(
    (currentEntityId) => {
      if (selectedAgentIds.length > 0) {
        return selectedAgentIds;
      }

      return [normaliseAgentId(currentEntityId)];
    },
    [normaliseAgentId, selectedAgentIds]
  );

  const handleSearch = useCallback(
    async (event) => {
      event.preventDefault();

      try {
        setIsLoading(true);
        const filter = {
          // Search rule: keep scope empty unless the user explicitly selected cityscope values.
          scope: toSelectedCityscopeCsv(selectedCityscope),
          agent: agentName || "",
          followupdate: followUpDate ? Moment(followUpDate).format() : "",
        };

        const localAgents = await getAgents(filter);
        setAgents(localAgents);
        setSelectedAgentIds(
          localAgents
            .filter((row) => row.selected)
            .map((row) => normaliseAgentId(row.entity_id_agent))
        );
        setIsLoading(false);
      } catch (error) {
        handleError(error);
        setIsLoading(false);
      }
    },
    [agentName, followUpDate, getAgents, handleError, normaliseAgentId, selectedCityscope, toSelectedCityscopeCsv]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("selected", {
        id: "selected",
        header: "",
        size: 70,
        meta: { align: "center" },
        cell: (cellInfo) => {
          const entityId = cellInfo.row.original.entity_id_agent;
          const isChecked = selectedAgentIds.includes(normaliseAgentId(entityId));

          return (
            <Form.Check
              type="checkbox"
              checked={isChecked}
              onChange={(event) => toggleSelected(entityId, event.target.checked)}
            />
          );
        },
        enableSorting: false,
      }),
      columnHelper.accessor("entity_id_agent", {
        id: "entity_id_agent",
        header: "Entity ID",
        size: 150,
        cell: (cellInfo) => {
          const entityId = cellInfo.getValue();
          const cityscopeCsv = toCityscopeCsv(selectedCityscope, cityscopeOptions);
          const followUpDateValue = followUpDate
            ? Moment(followUpDate).format()
            : Moment().format();

          return (
            <Link
              to={`/queues/salesheets/${entityId || 0}`}
              state={{
                followupdate: followUpDateValue,
                cityscope: cityscopeCsv,
                agentids: getBrowseListAgentIds(entityId),
              }}
            >
              {entityId || "(No Agent)"}
            </Link>
          );
        },
      }),
      columnHelper.accessor("agent", {
        id: "agent",
        header: "Agent",
        size: 340,
        cell: (cellInfo) => cellInfo.getValue() || <i>(No Agent)</i>,
      }),
      columnHelper.accessor("telephone", {
        id: "telephone",
        header: "Telephone",
        size: 200,
        cell: (cellInfo) => cellInfo.getValue() || "",
      }),
      columnHelper.accessor("sale_sheet_count", {
        id: "sale_sheet_count",
        header: "Sale Sheet Count",
        size: 160,
        meta: { align: "right" },
      }),
    ],
    [cityscopeOptions, followUpDate, getBrowseListAgentIds, normaliseAgentId, selectedAgentIds, selectedCityscope, toCityscopeCsv, toggleSelected]
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="SaleSheetFollowUpQueue">
      <Modal show={showAlert} onHide={() => setShowAlert(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Uh Oh!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">{errorMessage}</Alert>
        </Modal.Body>
      </Modal>

      <Form onSubmit={handleSearch} className="mb-2 salesheet-followup-filters">
        <Row className="g-2 align-items-end">
          <Col xs={12} md={4} lg={3} className="salesheet-cityscope-col">
            <Form.Group controlId={createId("filter", "cityscope")}>
              <Form.Label>Cityscope</Form.Label>
              <Select
                classNamePrefix="salesheet-filter-select"
                isMulti
                isClearable
                options={cityscopeOptions}
                value={selectedCityscope}
                onChange={(value) => setSelectedCityscope(value || [])}
                placeholder="Leave empty for all"
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={5} lg={5}>
            <Form.Group controlId={createId("filter", "agent")}> 
              <Form.Label>Agent Name</Form.Label>
              <Form.Control
                className="salesheet-agent-input"
                type="text"
                value={agentName}
                onChange={(event) => setAgentName(event.target.value)}
                placeholder="Contains"
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={3} lg={2}>
            <Form.Group controlId={createId("filter", "followupdate")}>
              <Form.Label>Follow Up Before</Form.Label>
              <DatePicker
                selected={followUpDate}
                onChange={(value) => setFollowUpDate(value)}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                isClearable
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={12} lg={1} className="d-grid">
            <Button type="submit" variant="primary" id={createId("action", "search")}>
              Search
            </Button>
          </Col>
        </Row>
      </Form>

      <Row className="mb-2">
        <Col>
          <Button variant="primary" type="button" size="sm" className="me-2" onClick={selectAll}>
            Select All
          </Button>
          <Button variant="primary" type="button" size="sm" onClick={deselectAll}>
            Deselect All
          </Button>
        </Col>
      </Row>

      {isLoading ? (
        <div className="outer-div">
          <div className="sweet-loading inner-div">
            <PacmanLoader
              cssOverride={{ display: "block", margin: "0 auto" }}
              size={20}
              color="#e03c31"
              loading={isLoading}
            />
          </div>
        </div>
      ) : (
        <GenericTable
          data={agents}
          columns={columns}
          section="salesheet-followup-queue"
          pageSize={PAGE_SIZES.saleSheets}
          elementIds={tableElementIds}
          enablePagination
          hasHeaderSorting
        />
      )}
    </div>
  );
}

export default SaleSheetFollowUpQueue;